import { DrawingEntity } from 'domain/entities/DrawingEntity';
import {
  type KetFileMultitailArrowNode,
  type MultitailArrowsReferencePositions,
  MultitailArrow as MicromoleculeMultitailArrow,
} from 'domain/entities/multitailArrow';
import { FixedPrecisionCoordinates } from 'domain/entities/fixedPrecision';
import type { Pool } from 'domain/entities/pool';
import { Vec2 } from 'domain/entities/vec2';
import type { KetFileNode } from 'domain/serializers/serializers.types';
import type { BaseRenderer } from 'application/render';
import type { MultitailArrowRenderer } from 'application/render/renderers/MultitailArrowRenderer';

export class MultitailArrow extends DrawingEntity {
  public renderer?: MultitailArrowRenderer = undefined;
  public arrowId?: number;

  constructor(
    private spineTopX: FixedPrecisionCoordinates,
    private spineTopY: FixedPrecisionCoordinates,
    private readonly height: FixedPrecisionCoordinates,
    private readonly headOffsetX: FixedPrecisionCoordinates,
    private readonly headOffsetY: FixedPrecisionCoordinates,
    private readonly tailLength: FixedPrecisionCoordinates,
    private readonly tailsYOffset: Pool<FixedPrecisionCoordinates>,
  ) {
    super();
  }

  public get center(): Vec2 {
    return Vec2.centre(
      new Vec2(
        this.spineTopX.sub(this.tailLength).getFloatingPrecision(),
        this.spineTopY.getFloatingPrecision(),
      ),
      new Vec2(
        this.spineTopX.add(this.headOffsetX).getFloatingPrecision(),
        this.spineTopY.add(this.height).getFloatingPrecision(),
      ),
    );
  }

  public setRenderer(renderer: MultitailArrowRenderer): void {
    super.setBaseRenderer(renderer as BaseRenderer);
    this.renderer = renderer;
  }

  public override moveRelative(delta: Vec2): void {
    this.spineTopX = this.spineTopX.add(
      FixedPrecisionCoordinates.fromFloatingPrecision(delta.x),
    );
    this.spineTopY = this.spineTopY.add(
      FixedPrecisionCoordinates.fromFloatingPrecision(delta.y),
    );
  }

  public override moveAbsolute(position: Vec2) {
    const delta = Vec2.diff(
      position,
      new Vec2(this.spineTopX.value, this.spineTopY.value),
    );

    this.moveRelative(delta);
  }

  static fromKet(
    multitailArrowKetNode: KetFileNode<KetFileMultitailArrowNode>,
  ) {
    const {
      spineTopX,
      spineTopY,
      height,
      headOffsetX,
      headOffsetY,
      tailsLength,
      tailsYOffset,
    } = MicromoleculeMultitailArrow.getConstructorParamsFromKetNode(
      multitailArrowKetNode,
    );

    return new MultitailArrow(
      spineTopX,
      spineTopY,
      height,
      headOffsetX,
      headOffsetY,
      tailsLength,
      tailsYOffset,
    );
  }

  toKetNode(): KetFileNode<KetFileMultitailArrowNode> {
    return MicromoleculeMultitailArrow.getParametersForKetNode(
      this.spineTopX,
      this.spineTopY,
      this.headOffsetX,
      this.headOffsetY,
      this.tailLength,
      this.tailsYOffset,
      this.height,
      this.center,
      false,
    );
  }

  getReferencePositions(): MultitailArrowsReferencePositions {
    return MicromoleculeMultitailArrow.getReferencePositions(
      this.spineTopX,
      this.spineTopY,
      this.height,
      this.headOffsetX,
      this.headOffsetY,
      this.tailLength,
      this.tailsYOffset,
    );
  }
}
